import { Close, Save } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGridPro as DataGrid, GridColDef } from '@mui/x-data-grid-pro';
import { Loader } from 'components';
import ButtonSwitcher from 'components/ButtonSwitcher';
import ToggleButton from 'components/ToggleButton';
import useWindowDimensions from 'hooks/useWindowDimensions';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  changeDisplayModeAction,
  injectSummaryMonthAction,
  injectSummaryQuarterAction,
  setSummaryColumnsMonths,
  setSummaryColumnsQuarters,
  toggleIsSummaryDeployedAction,
} from 'store/actions/catalogToOrderActions';
import { setSnackbarAction } from 'store/actions/snackbarActions';
import { useSelector } from 'store/hooks';
import { useAppState } from 'store/Provider';
import { RootState } from 'store/redux';
import { DisplaySummaryMode } from 'types/enums/DisplaySummaryMode';
import { WorkunitMonth } from 'features/Orders/OrderWorkunits/helpersOrderWorkunits';
import useOrderWorkunitApi from 'features/Orders/OrderWorkunits/services/useOrderWorkunitApi';
import useOrderWorkunitsService from 'features/Orders/OrderWorkunits/services/useOrderWorkunitsService';

import './SummaryDataGrid.scss';
import useSummaryDatagridHelper from './useSummaryDatagridHelper';

export type ComputedWorkunits = {
  date?: string;
  total: number;
  randomId: number;
  title: string;
};

export interface WorkunitComplexityMonths extends WorkunitMonth {
  [x: number]: number;
}

const SummaryDataGrid = () => {
  const [t] = useTranslation();
  const loadings = useSelector((state) => state.loadings);

  const { state, dispatch } = useAppState();
  const { getColumnsMonths, getQuarterColumns, getValuesByMonthsBaseLine } = useSummaryDatagridHelper();
  const { computeSummaryMonthsBaseLine, redirectToPreview, computeSummaryQuartersBaseLine } =
    useOrderWorkunitsService();
  const { updateOrderStatus, bulkCreateOrderWorkunits, exportExcel, getCatalogComplexities, getTenantWorkloads } =
    useOrderWorkunitApi();
  const { tagFilter } = useSelector((state) => state.changeBaseline);
  const selectedOrderWorkunits = useSelector((state: RootState) => state.catalogToOrder.selectedWorkunits);
  const order = useSelector((state) => state.catalogToOrder.order);
  const sliceMonth = useSelector((state) => state.catalogToOrder.summaryHandler.monthSlice);
  const sliceQuarter = useSelector((state) => state.catalogToOrder.summaryHandler.quarterSlice);
  const selectedScope = useSelector((state) => state.catalogToOrder.selectedScope);
  const months = useSelector((state) => state.catalogToOrder.months);
  const isOpen = useSelector((state) => state.catalogToOrder.summaryHandler.isOpen);
  const displayMode = useSelector((state) => state.catalogToOrder.summary.displayMode);
  const columnsMonths = useSelector((state) => state.catalogToOrder.summary.columnsMonths);
  const columnsQuarter = useSelector((state) => state.catalogToOrder.summary.columnsQuarter);
  const buttonsLoading = useSelector((state) => state.loadings.summary);
  const valuesMonths = useSelector((state) => state.catalogToOrder.summary.valuesMonths);
  const valuesQuarters = useSelector((state) => state.catalogToOrder.summary.valuesQuarters);
  const isDisplayModeMonthly = useCallback(() => displayMode === DisplaySummaryMode.MONTHLY, [displayMode]);

  useEffect(() => {
    if (order?.catalog_id) {
      getCatalogComplexities(order?.catalog_id);
      getTenantWorkloads(order);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order?.catalog_id, months.length]);

  useEffect(() => {
    getValuesByMonthsBaseLine(tagFilter);
  }, [getValuesByMonthsBaseLine, tagFilter]);

  useEffect(() => {
    if (order) {
      if (displayMode === DisplaySummaryMode.MONTHLY) {
        dispatch(injectSummaryMonthAction(computeSummaryMonthsBaseLine(tagFilter) ?? []));
      }
      if (displayMode === DisplaySummaryMode.QUARTERLY) {
        dispatch(injectSummaryQuarterAction(computeSummaryQuartersBaseLine(tagFilter) ?? []));
      }
    }
    // setUsedQuartersWorkunits(() => computeSummaryMonthsBaseLine);
  }, [order, dispatch, computeSummaryMonthsBaseLine, computeSummaryQuartersBaseLine, displayMode]);

  useEffect(() => {
    if (isDisplayModeMonthly()) {
      dispatch(setSummaryColumnsMonths(getColumnsMonths() as GridColDef[]));
    }
    if (!isDisplayModeMonthly()) {
      dispatch(setSummaryColumnsQuarters(getQuarterColumns() as GridColDef[]));
    }
  }, [
    sliceMonth,
    months,
    sliceQuarter,
    selectedScope,
    dispatch,
    getColumnsMonths,
    getQuarterColumns,
    isDisplayModeMonthly,
  ]);

  const gridWrapperRef = React.useRef<HTMLDivElement>(null);
  // React.useLayoutEffect(() => {
  //   if (!gridWrapperRef?.current) return;
  //   const gridDiv = gridWrapperRef?.current;
  //   if (!gridDiv) return;
  //   if (gridDiv) {
  //     const gridEl: HTMLDivElement = gridDiv.querySelector('div')!;
  //     if (!gridEl) return;
  //     gridEl.style.height = '';
  //   }
  // });

  const dimensions = useWindowDimensions();

  return (
    <Box
      display="flex"
      flexWrap={{ lg: 'nowrap', xs: 'wrap' }}
      height="30vh"
      sx={{ bgcolor: 'background.paper' }}
      gap={4}
    >
      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'flex-start',
        }}
      >
        <ButtonSwitcher size="small">
          <Button
            variant={displayMode === 'MONTHLY' ? 'contained' : 'text'}
            color={displayMode === 'MONTHLY' ? 'primary' : 'neutral'}
            onClick={() => dispatch(changeDisplayModeAction(DisplaySummaryMode.MONTHLY))}
          >
            {t('Monthly')}
          </Button>
          <Button
            variant={displayMode === 'QUARTERLY' ? 'contained' : 'text'}
            color={displayMode === 'QUARTERLY' ? 'primary' : 'neutral'}
            onClick={() => dispatch(changeDisplayModeAction(DisplaySummaryMode.QUARTERLY))}
          >
            {t('Quarterly')}
          </Button>
        </ButtonSwitcher>
      </Box>
      {isDisplayModeMonthly() && (
        <DataGrid
          rows={valuesMonths}
          columns={columnsMonths as GridColDef[]}
          checkboxSelection={false}
          disableSelectionOnClick
          disableColumnFilter
          disableColumnSelector
          disableColumnMenu
          disableColumnResize
          hideFooter
          autoHeight={false} // !!! Crucial: Disable autoHeight
          headerHeight={70} // Keep header height
          rowHeight={dimensions.currentHeight > 800 ? 37 : 26} // Keep row height
          getRowId={(row) => row.randomId}
          className="summary-datagrid baseline"
        />
      )}
      {!isDisplayModeMonthly() && (
        <DataGrid
          rows={state.catalogToOrder.summary.valuesQuarters}
          columns={columnsQuarter as GridColDef[]}
          checkboxSelection={false}
          disableSelectionOnClick
          disableColumnFilter
          disableColumnSelector
          disableColumnMenu
          hideFooter
          autoHeight={false} // !!! Crucial: Disable autoHeight
          pageSize={100} // Keep pageSize if desired, but know it enables internal scroll if rows > 100
          headerHeight={70}
          rowHeight={dimensions.currentHeight > 800 ? 45 : 32}
          getRowId={(row) => row.randomId}
          className="summary-datagrid baseline"
        />
      )}
    </Box>
  );
};

export default SummaryDataGrid;
